import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, Badge } from "@/components/ui-hud";
import { MEMBER_TYPES } from "@/data/pjp";
import Telemetry from "@/components/Telemetry";
import PJPCoin from "@/components/PJPCoin";
import MatrixRain from "@/components/MatrixRain";
import { ArrowUpRight, ShieldCheck, Sparkles, Wrench, Plane, TrendingUp, Zap, Globe2, Users, MessageSquare, Wallet, Radio, Fuel, FileText, BookOpen, Store, Send, Search as SearchIcon, ArrowRight, Calendar as CalIcon, DollarSign } from "lucide-react";

// Cinematic aviation photography — dark G600-style hero
const HERO_IMG = "https://images.unsplash.com/photo-1619659085985-f51a00f0160a?crop=entropy&cs=srgb&fm=jpg&w=2400&q=90";
const AEROINTEL_IMG = "https://images.unsplash.com/photo-1583997065462-92b23bffcb92?w=1600&q=88";
const NIGHT_TARMAC = "https://images.unsplash.com/photo-1569629743817-70d8db6c323b?w=1600&q=88";

export default function Landing() {
  const [stats, setStats] = useState({ members: 26511, contracts_open: 0, aog_active: 0, marketplace_listings: 0, forum_topics: 0 });
  const [pub, setPub] = useState({ fbos: 0, operators: 0, aircraft_for_sale: 0, pjp_fuel_tails: 0, pjp_fuel_target: 2500, members: 0 });
  const [founder, setFounder] = useState({ token: null, name: null, member_number: null });
  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/public/stats").then((r) => setPub(r.data)).catch(() => {});
    api.get("/public/founder-badge").then((r) => setFounder(r.data || {})).catch(() => {});
  }, []);

  return (
    <div>
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden">
        {/* Video background — your private jet overhead, muted looping */}
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster="/videos/hero-poster.jpg"
          className="absolute inset-0 h-full w-full object-cover"
        >
          <source src="/videos/hero-flight.mp4" type="video/mp4" media="(min-width: 768px)" />
          <source src="/videos/hero-flight-sm.mp4" type="video/mp4" />
        </video>
        {/* Poster fallback layer for slow connections */}
        <div
          className="absolute inset-0 -z-10"
          style={{
            backgroundImage: `url(/videos/hero-poster.jpg)`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0" style={{
          background: "linear-gradient(180deg, rgba(10,15,26,0.25) 0%, rgba(10,15,26,0.55) 55%, rgba(10,15,26,0.98) 100%)"
        }} />

        {/* HUD grid + scan line accent */}
        <div className="absolute inset-0 hud-grid opacity-40 pointer-events-none" />
        <div className="scan-line" />

        <div className="relative max-w-7xl mx-auto px-6 pt-20 md:pt-24 pb-32 md:pb-40 z-10 grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-8">
            <div className="animate-fade-in flex items-center gap-3 mb-8">
              <span className="status-pill">PJP · Live</span>
              <span className="mono text-[10px] text-white/45 tracking-widest">V4.2 · {new Date().toISOString().slice(0, 10)}</span>
            </div>
            <h1 className="font-display font-semibold text-5xl md:text-7xl lg:text-[100px] tracking-[-0.03em] leading-[0.94] text-white animate-fade-up">
              Aviation moves<br />
              at the speed of<br />
              <span className="text-sky">connection.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-white/75 max-w-2xl leading-relaxed animate-fade-up" style={{ animationDelay: "0.15s" }}>
              The operating network of private aviation. Pilots, operators, flight departments,
              mechanics, and brokers — synchronized in real-time.
            </p>
            <div className="mt-10 flex flex-wrap gap-3 animate-fade-up" style={{ animationDelay: "0.3s" }}>
              <Link
                to="/register"
                data-testid="hero-cta-join"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full mono text-sm font-bold tracking-wider transition-all hover:scale-[1.02]"
                style={{ background: "#E8A257", color: "#1A1408", boxShadow: "0 0 24px rgba(232,162,87,0.35)" }}
              >
                JOIN FREE <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link
                to="/hire-now"
                data-testid="hero-cta-hire"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full mono text-sm font-bold tracking-wider transition-all hover:scale-[1.02]"
                style={{ background: "#2ED573", color: "#0A1F14", boxShadow: "0 0 24px rgba(46,213,115,0.35)" }}
              >
                HIRE NOW
              </Link>
              <Link
                to="/quote"
                data-testid="hero-cta-charter"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full mono text-sm font-bold tracking-wider transition-all hover:scale-[1.02]"
                style={{ background: "#4B9CFF", color: "#0A1220", boxShadow: "0 0 24px rgba(75,156,255,0.35)" }}
              >
                INSTANT PJ QUOTE
              </Link>
              <Link to="/search" data-testid="hero-cta-search" className="btn">Search Crew</Link>
            </div>

            {/* Metric strip — primary members/global stats */}
            <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-8 lg:gap-x-10 max-w-4xl">
              {[
                { n: (Math.max(pub.members || 0, stats.members || 26511)).toLocaleString() + "+", l: "Active members", tag: "MEMBERS" },
                { n: "120+", l: "Countries", tag: "GEO" },
                { n: "1,200+", l: "Industry roles", tag: "ROLES" },
                { n: "24/7", l: "Ops network", tag: "STATUS" },
              ].map((s, i) => (
                <div key={i} className="animate-fade-up min-w-0" style={{ animationDelay: `${0.4 + i * 0.08}s` }}>
                  <div className="mono text-[9px] text-sky/70 tracking-[0.28em] mb-2">// {s.tag}</div>
                  <div className="font-display font-semibold text-[28px] sm:text-[32px] lg:text-[40px] xl:text-5xl text-white tracking-tight leading-none whitespace-nowrap">{s.n}</div>
                  <div className="mt-2 text-[13px] text-white/55">{s.l}</div>
                </div>
              ))}
            </div>

            {/* Secondary strip — live directory counters */}
            <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-6 lg:gap-x-10 max-w-4xl" data-testid="landing-directory-stats">
              {[
                { n: pub.fbos.toLocaleString(), l: "FBOs indexed globally", tag: "FBO NETWORK", tone: "sky" },
                { n: pub.operators.toLocaleString(), l: "Part 91K/135 operators", tag: "OPERATORS", tone: "sky" },
                { n: pub.aircraft_for_sale.toLocaleString(), l: "Aircraft for sale", tag: "MARKETPLACE", tone: "gold" },
                { n: "2,500+", l: "PJP Fuel tails registered", tag: "PJP FUEL", tone: "avail" },
              ].map((s, i) => (
                <div key={i} className="animate-fade-up min-w-0" style={{ animationDelay: `${0.7 + i * 0.08}s` }} data-testid={`stat-${s.tag.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  <div className={`mono text-[9px] ${s.tone === 'avail' ? 'text-avail/80' : s.tone === 'gold' ? 'text-champagne/80' : 'text-sky/70'} tracking-[0.28em] mb-2`}>// {s.tag}</div>
                  <div className={`font-display font-semibold text-[22px] sm:text-[26px] lg:text-[30px] tracking-tight leading-tight ${s.tone === 'avail' ? 'text-avail' : s.tone === 'gold' ? 'text-champagne' : 'text-white'}`}>{s.n}</div>
                  <div className="mt-2 text-[12px] text-white/55">{s.l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Telemetry sidebar */}
          <div className="md:col-span-4 hidden md:block animate-fade-up" style={{ animationDelay: "0.45s" }}>
            {/* 3D spinning PJP coin — Jeff's custom emblem */}
            <div
              data-testid="hero-coin-wrap"
              className="relative mb-6"
              style={{ height: 320 }}
            >
              <div
                className="absolute inset-0 rounded-2xl"
                style={{
                  background:
                    "radial-gradient(circle at 50% 45%, rgba(75,156,255,0.18) 0%, rgba(10,15,26,0) 60%)",
                }}
              />
              <PJPCoin speed={0.2} tilt={6} />
            </div>
            <Telemetry />
          </div>
        </div>
      </section>

      {/* ============ WHY PJP — three column tech-forward pitch ============ */}
      <section className="max-w-7xl mx-auto px-6 py-24 md:py-32">
        <div className="grid md:grid-cols-2 gap-16 items-end mb-16">
          <div>
            <div className="eyebrow mb-6">What we do</div>
            <h2 className="font-display font-semibold text-4xl md:text-6xl tracking-tight leading-[1.05] text-white">
              We're not a<br />directory. We're<br />the network layer<br />of aviation ops.
            </h2>
          </div>
          <p className="text-lg text-white/70 leading-relaxed">
            When a Part 135 operator needs to staff a new Gulfstream before it enters service, a Part 91 management company loses a captain days before a trip, or an aircraft is AOG in Van Nuys at 2 a.m. — PJP gets the request in front of the right aviation professionals fast. From full-time pilots and contract crews to lovely flight attendants, mechanics and trusted industry connections, PJP keeps business aviation moving.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5 stagger">
          {[
            {
              icon: ShieldCheck,
              overline: "// PROFILE · YOUR DIGITAL LOGBOOK",
              title: "The most powerful crew profile in aviation.",
              desc:
                "Cross-checked against the FAA Airmen Registry (591K+ pilots). Your ratings, hours, type certs, medical, currency, licenses, insurance, resume, and endorsements — all verified, all in one place. Recruiters and operators trust it. You never fill out another PDF.",
              chips: ["FAA cross-checked", "OCR-verified docs", "Cert · Type · Class", "NFC Digital ID"],
              accent: "#4B9CFF",
              glow: "rgba(75,156,255,0.35)",
            },
            {
              icon: Zap,
              overline: "// OPPORTUNITIES · LIFE-CHANGING GIGS",
              title: "The gig that changes your career? It lands here first.",
              desc:
                "26,511 crew, thousands of Part 91 & 135 operators, contract dispatchers, brokers, and MRO shops — routing real-time openings to the exact profiles that match. Full-time seats, contract flying, AOG mechanic alerts, cabin crew assignments — surfaced by an AI radar tuned to your ratings, home base, and availability.",
              chips: ["Job Radar AI", "AOG SMS blasts", "Type-rating scored", "One-tap apply"],
              accent: "#2ED573",
              glow: "rgba(46,213,115,0.35)",
            },
            {
              icon: Sparkles,
              overline: "// HANGAR OF HIGH-END TOYS · UNLOCKED",
              title: "Access what used to take a rolodex — instantly.",
              desc:
                "Free JumpSeat Program (a $10K/yr rider perk — free for crew). Instant charter quotes with 3D globe routing, real-world winds & tankering math. AeroIntel — a GPT-5.2 aviation copilot trained on FARs, POHs, and 26,500 professional crews. PJP Wallet payments. Digital ID badge. Directory of every operator, FBO and MRO worth knowing.",
              chips: ["JumpSeat · FREE", "AeroIntel GPT-5.2", "3D Quote Engine", "PJP Wallet"],
              accent: "#F4C443",
              glow: "rgba(244,196,67,0.35)",
            },
          ].map((f, i) => (
            <div
              key={f.title}
              data-testid={`landing-pillar-${i + 1}`}
              className="relative rounded-2xl border p-7 md:p-8 overflow-hidden group transition-all hover:-translate-y-1"
              style={{
                borderColor: `${f.accent}55`,
                background: `linear-gradient(160deg, ${f.accent}18 0%, rgba(5,8,16,0.92) 55%)`,
                boxShadow: `0 0 40px -20px ${f.glow}`,
              }}
            >
              {/* Corner brackets */}
              <span className="absolute top-3 left-3 w-3 h-3 border-t border-l" style={{ borderColor: f.accent }} />
              <span className="absolute top-3 right-3 w-3 h-3 border-t border-r" style={{ borderColor: f.accent }} />
              <span className="absolute bottom-3 left-3 w-3 h-3 border-b border-l" style={{ borderColor: f.accent }} />
              <span className="absolute bottom-3 right-3 w-3 h-3 border-b border-r" style={{ borderColor: f.accent }} />

              {/* Number + overline */}
              <div className="flex items-baseline gap-3 mb-4">
                <div
                  className="font-display font-bold text-3xl leading-none tabular-nums"
                  style={{ color: f.accent, textShadow: `0 0 12px ${f.glow}` }}
                >
                  0{i + 1}
                </div>
                <div className="mono text-[9.5px] tracking-[0.22em] uppercase" style={{ color: `${f.accent}CC` }}>
                  {f.overline}
                </div>
              </div>

              {/* Icon */}
              <div
                className="h-12 w-12 rounded-xl border flex items-center justify-center mb-5"
                style={{ borderColor: `${f.accent}66`, background: `${f.accent}18` }}
              >
                <f.icon className="h-5 w-5" style={{ color: f.accent }} />
              </div>

              {/* Title + body */}
              <h3 className="font-display font-semibold text-2xl md:text-[26px] tracking-tight mb-3 text-white leading-[1.15]">
                {f.title}
              </h3>
              <p className="text-white/65 text-[14.5px] leading-relaxed">{f.desc}</p>

              {/* Chips */}
              <div className="mt-5 flex flex-wrap gap-1.5">
                {f.chips.map((c) => (
                  <span
                    key={c}
                    className="mono text-[9.5px] tracking-widest uppercase px-2 py-1 rounded-full border"
                    style={{ borderColor: `${f.accent}44`, background: `${f.accent}0F`, color: `${f.accent}` }}
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Brag row — the elevator pitch for a crew member stumbling in */}
        <div className="mt-14 rounded-2xl border border-white/10 bg-black/40 backdrop-blur px-6 md:px-8 py-6 grid md:grid-cols-4 gap-6">
          <BragStat value="26,511" label="Verified members" tone="#4B9CFF" />
          <BragStat value="591,782" label="FAA airmen cross-checked" tone="#2ED573" />
          <BragStat value="163" label="Airports live-metered" tone="#F4C443" />
          <BragStat value="$10K" label="JumpSeat perk · FREE for crew" tone="#C99BFF" />
        </div>
      </section>

      {/* ============ EVERY TOOL YOU'LL NEED · under one roof (10 features) ============ */}
      <section className="border-t border-white/[0.06] py-24 md:py-28" data-testid="feature-tour">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <div className="mono text-[10px] tracking-[0.28em] uppercase text-cyan/80">
              // EVERY TOOL YOU'LL NEED · UNDER ONE ROOF
            </div>
            <h2 className="font-display font-semibold text-4xl md:text-6xl tracking-tight leading-[1.05] text-white mt-4">
              Ten interlocking systems.<br />
              <span className="text-white/60">One badge unlocks it all.</span>
            </h2>
            <p className="text-white/60 text-lg mt-5 leading-relaxed">
              Everything below runs on the same identity, the same wallet, the same directory. No 8 apps, no 12 logins.
              Just a hangar full of high-end toys — waiting for the tail number on your PJP ID.
            </p>
          </div>

          <div className="space-y-24 md:space-y-32">
            {/* 01 · HOT MIC */}
            <FeatureRow
              idx="01"
              accent="#C99BFF"
              overline="// HOT MIC · THE FREQUENCY EVERYONE'S TALKING ON"
              title="26,500 aviators in one hangar, always on."
              body="Ask a G550 Fadec question at 7 a.m. and get answers from three IAs before you finish your coffee. Trade duty-day war stories with contract captains who've flown every fleet you're studying. Get real intel — not LinkedIn cheerleading. This is the pilot bar you always wished existed, minus the tab."
              chips={["26,511 members", "Topic threads", "Op-verified", "Anonymous DMs"]}
              linkTo="/forum"
              linkLabel="Enter Hot Mic"
              mock={<MockHotMic />}
              flip={false}
            />

            {/* 02 · PJP WALLET */}
            <FeatureRow
              idx="02"
              accent="#2ED573"
              overline="// PJP WALLET · MONEY IN THE COCKPIT"
              title="Every dollar you earn on PJP — collected up front, paid guaranteed."
              body="Contract flying? Empty-leg fill? Referral bonus? It lands in your PJP Wallet the second the trip is confirmed — clients wire us, we hold escrow, you get paid. Cash out to your bank when Stripe Connect ships. No more chasing invoices, no more 45-day nets, no more 'the check is in the mail'."
              chips={["Escrow-backed", "Instant balance", "Referral bounties", "1099 export"]}
              linkTo="/register"
              linkLabel="Open Wallet"
              mock={<MockWallet />}
              flip={true}
            />

            {/* 03 · UNICOM */}
            <FeatureRow
              idx="03"
              accent="#4B9CFF"
              overline="// UNICOM · TAIL-NUMBER → FBO"
              title="Ping the FBO from the flight deck — by tail number."
              body="Fuel orders, ground transport, lav service, GPU, catering, hangar hold, deicing, custom clearance — order everything the aircraft needs at the destination from a single message thread keyed to your tail number. FBO sees the request the moment you send it, confirms directly, and the whole exchange is logged against the flight. No more calling 20 minutes out on 122.95 hoping someone hears you."
              chips={["Tail-number thread", "Fuel · GPU · lav", "Ground transport", "Catering & customs"]}
              linkTo="/inbox"
              linkLabel="Open Unicom"
              mock={<MockUnicom />}
              flip={false}
            />

            {/* 04 · PJP FUEL */}
            <FeatureRow
              idx="04"
              accent="#F4C443"
              overline="// PJP FUEL · SAVE $40K/YR PER TAIL"
              title="Live fuel prices at 163 airports, refreshed every hour."
              body="Public-posted retail vs. PJP-negotiated contract fuel — side by side. Tanker from KOPF for $2.11/gal instead of paying $6.85 at KASE. Auto-suggest tankering opportunities on your quote. Operators shave five-figure fuel bills off every quarter, dispatchers plan smarter, riders pay less. Nobody gets gouged."
              chips={["163 airports", "Contract fuel", "Auto-tankering", "Hourly refresh"]}
              linkTo="/fuel"
              linkLabel="See Fuel Prices"
              mock={<MockFuel />}
              flip={true}
            />

            {/* 05 · PHF */}
            <FeatureRow
              idx="05"
              accent="#FF6B9D"
              overline="// PHF · YOUR PILOT HISTORY, ONCE"
              title="Fill out your PHF one time. Never touch a PDF again."
              body="The Pilot History Form every insurer, every operator, every management company asks for — hours by category, aircraft-specific PIC/SIC totals, accidents & incidents, employers, license actions. Your PHF auto-populates every insurance quote, every 135 hire, every management transition on PJP. Save it once. Save it forever."
              chips={["Auto-fills quotes", "Insurance-ready", "Encrypted vault", "PDF export"]}
              linkTo="/phf"
              linkLabel="Build my PHF"
              mock={<MockPHF />}
              flip={false}
            />

            {/* 06 · LOGBOOK */}
            <FeatureRow
              idx="06"
              accent="#5FE895"
              overline="// LOGBOOK · DIGITAL + BULLETPROOF"
              title="Your Part 61.51 logbook — cross-linked to FAA, insurers, and every job you apply for."
              body="Log a leg, tag the tail, drop a departure/arrival, and PJP builds the running totals you actually need: PIC/SIC by type, night, IFR, cross-country, currency clocks ticking, medical expiration counter, BFR due date. Recruiters see your live hours the second you log them. No more Excel. No more paper. No more panic at 91.1097."
              chips={["Part 61.51 compliant", "Live currency", "Type-specific totals", "Recruiter-visible"]}
              linkTo="/logbook"
              linkLabel="Open Logbook"
              mock={<MockLogbook />}
              flip={true}
            />

            {/* 07 · AIRCRAFT SALES */}
            <FeatureRow
              idx="07"
              accent="#FFD86B"
              overline="// AIRCRAFT SALES · THE INSIDE MARKETPLACE"
              title="Listings so sweet you can taste them. Connect and be involved with the transaction."
              body="Skip the broker-brokered-broker chain. PJP Aircraft Sales is a first-look board for tail numbers coming to market — often before Controller and JetNet have them indexed. Pre-buy walk-arounds by verified A&Ps. Photos from actual crew. Squawks disclosed up front. Our brokers listing on PJP are happy to commission members for bringing a qualified buyer — spot a match in the network, make the intro, get paid."
              chips={["Pre-market listings", "Verified A&P walks", "Squawk transparency", "Referral commissions"]}
              linkTo="/aircraft-sales"
              linkLabel="Browse Aircraft"
              mock={<MockAircraftSales />}
              flip={false}
            />

            {/* 08 · CHARTER DIRECT QUOTES */}
            <FeatureRow
              idx="08"
              accent="#4B9CFF"
              overline="// INSTANT PJ QUOTE · 3D · WIND-CORRECTED · BROKER-FREE"
              title="Retail charter, without the retail markup."
              body="Multi-leg trip builder with a live 3D globe, real-world winds pulled from Open-Meteo, tankering math baked into every price, and operator bids landing directly in your inbox. Skip the 15% broker cut. Skip the phone tag. Get a quote in 90 seconds — same fleet, same crews, same aircraft the brokers would've called anyway."
              chips={["3D globe routing", "Live winds", "Tankering math", "Direct bids"]}
              linkTo="/quote"
              linkLabel="Get Instant Quote"
              mock={<MockQuote />}
              flip={true}
            />

            {/* 09 · JUMPSEAT */}
            <FeatureRow
              idx="09"
              accent="#2ED573"
              overline="// JUMPSEAT · FREE FOR CREW"
              title="Turn every empty leg into value — operators ferry crews at no additional cost, crew members JumpSeat free with their PJP membership, and Rider members enjoy priority access to exclusive JumpSeat opportunities."
              body="Every week, operators reposition aircraft with empty seats. PJP transforms those ferry flights into value by giving operators a simple way to move crew at no additional cost, providing complimentary travel for working crew members through their PJP membership, and offering Rider members exclusive access to premium JumpSeats before they're publicly available. Everyone benefits from flights that were already taking place."
              chips={["Crews JumpSeat free", "Live calendar", "1-click reserve", "Priority alerts"]}
              linkTo="/empty-leg"
              linkLabel="See JumpSeats"
              mock={<MockEmptyLegs />}
              flip={false}
            />

            {/* 10 · FASTEST CREW SEARCH */}
            <FeatureRow
              idx="10"
              accent="#FF7A7A"
              overline="// FIND-CREW · FASTEST SEARCH IN AVIATION"
              title="Fill a Crew position in 90 seconds."
              body="Crewing an aircraft shouldn't feel like detective work. Search by aircraft type, home base, availability, certifications, and FAA verification, then instantly notify every qualified crew member with your trip details attached. No more phone tag. No more unanswered texts. No more wondering who's actually available. Just verified professionals, real-time availability, and a confirmed crew in minutes."
              chips={["FAA-verified only", "Type-rating scored", "One-tap DM", "AOG SMS blast"]}
              linkTo="/search"
              linkLabel="Search Crew"
              mock={<MockCrewSearch />}
              flip={true}
            />
          </div>

          {/* Bottom CTA row */}
          <div className="mt-24 md:mt-32 rounded-3xl border border-cyan/30 p-8 md:p-12 text-center relative overflow-hidden"
            style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(75,156,255,0.18) 0%, rgba(5,8,16,0.9) 55%)" }}>
            <div className="mono text-[10px] tracking-[0.28em] uppercase text-cyan/80 mb-3">
              // ONE BADGE · ALL TEN SYSTEMS
            </div>
            <h3 className="font-display font-bold text-3xl md:text-5xl tracking-tight text-white leading-[1.05]">
              Still reading? You're already one of us.
            </h3>
            <p className="text-white/65 mt-4 max-w-2xl mx-auto text-[15px] leading-relaxed">
              If you've made it this far, you already understand what PJP is about. Join today and unlock Hot Mic,
              JumpSeat, your Digital ID, Crew Directory, and every professional tool built exclusively for aviation.
              Your account is ready in under 60 seconds.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link to="/register" data-testid="feature-tour-cta-join" className="btn btn-primary">
                <Sparkles className="h-4 w-4" /> Join Free
              </Link>
              <Link to="/membership" data-testid="feature-tour-cta-see-tiers" className="btn">
                PJP Pro · $129/yr <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FOUNDER'S LIVE PJP ID — showcase ============ */}
      {founder.token && (
      <section className="border-t border-white/[0.06] py-24 md:py-32" data-testid="founder-badge-section">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6">
            <div className="eyebrow mb-6">// PJP Digital ID · Live specimen</div>
            <h2 className="font-display font-semibold text-4xl md:text-6xl tracking-tight leading-[1.05] text-white mb-8">
              A verified identity<br />you can <span className="text-sky">flash on the ramp.</span>
            </h2>
            <p className="text-lg text-white/70 leading-relaxed mb-6">
              Every PJP member gets a shareable, NFC-ready Digital ID. Scan the QR
              at any FBO — the badge goes live for 60 seconds with a chasing green
              border, animated PJP coin, and cryptographically-linked FAA credentials.
            </p>
            <ul className="space-y-2.5 text-white/70 text-[15px]">
              <li className="flex items-start gap-2.5"><ShieldCheck className="h-4 w-4 text-avail mt-1 flex-shrink-0" /> Cross-checked against 591K+ pilots in the FAA Airmen Registry</li>
              <li className="flex items-start gap-2.5"><Zap className="h-4 w-4 text-sky mt-1 flex-shrink-0" /> Real-time ramp status — FBOs scan you in and out</li>
              <li className="flex items-start gap-2.5"><Sparkles className="h-4 w-4 text-orange-hud mt-1 flex-shrink-0" /> NFC-ready — tap your phone to any reader</li>
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to={`/id/${founder.token}`}
                data-testid="landing-view-founder-badge"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full mono text-sm font-bold tracking-wider transition-all hover:scale-[1.02]"
                style={{ background: "#4B9CFF", color: "#0A1220", boxShadow: "0 0 24px rgba(75,156,255,0.35)" }}
              >
                OPEN FULL BADGE <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link to="/register" data-testid="landing-get-your-id" className="btn">Get your PJP ID</Link>
            </div>
            {founder.name && (
              <div className="mt-6 mono text-[10px] tracking-[0.24em] text-white/40">
                PJP-ID {founder.member_number ? String(founder.member_number).padStart(5, "0") : "—"} · {founder.name.toUpperCase()} · FOUNDER
              </div>
            )}
          </div>

          <div className="md:col-span-6 relative">
            <div
              className="absolute -inset-6 rounded-3xl pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle at 50% 40%, rgba(46,213,115,0.14) 0%, rgba(10,15,26,0) 60%)",
              }}
            />
            <div
              className="relative mx-auto rounded-2xl overflow-hidden border border-white/10 shadow-2xl"
              style={{ maxWidth: 460, background: "#050810" }}
            >
              <iframe
                data-testid="landing-founder-badge-iframe"
                src={`/id/${founder.token}?embed=1`}
                title={`PJP Digital ID · ${founder.name || "Founder"}`}
                loading="lazy"
                className="block w-full"
                style={{ height: 940, border: 0, background: "transparent" }}
              />
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ============ MEMBER TYPES ============ */}
      <section className="border-t border-white/[0.06] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div className="max-w-2xl">
              <div className="eyebrow mb-5">The network</div>
              <h2 className="font-display font-semibold text-4xl md:text-6xl tracking-tight leading-[1.05] text-white">
                Built for every seat<br />in private aviation.
              </h2>
            </div>
            <Link to="/directory" data-testid="link-full-directory" className="btn btn-ghost">
              Full directory <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 stagger">
            {MEMBER_TYPES.map((m) => (
              <Link key={m.slug} to={`/directory/${m.slug}`} data-testid={`member-type-${m.slug}`} className="group">
                <Card className="h-full !p-8">
                  <div className="eyebrow-muted mb-4 text-sky !normal-case tracking-normal font-medium">{m.kicker}</div>
                  <h3 className="font-display font-semibold text-2xl md:text-3xl tracking-tight mb-3 text-white">{m.label}</h3>
                  <p className="text-white/60 text-[15px] leading-relaxed mb-8">{m.desc}</p>
                  <div className="inline-flex items-center gap-2 text-sky text-sm font-medium">
                    Meet the members
                    <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============ AEROINTEL ============ */}
      <section className="relative border-t border-white/[0.06] overflow-hidden py-24 md:py-32">
        {/* Matrix rain — blue variant, sits behind everything */}
        <MatrixRain className="opacity-[0.5]" />
        {/* Vignette so text remains readable */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at 50% 40%, rgba(5,8,16,0.55) 0%, rgba(5,8,16,0.85) 55%, rgba(5,8,16,0.97) 100%)",
          }}
        />
        <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{ backgroundImage: `linear-gradient(180deg, rgba(10,15,26,0.6), rgba(10,15,26,1)), url(${AEROINTEL_IMG})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="relative max-w-7xl mx-auto px-6 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="chip">New · AeroIntel</span>
              <span className="chip chip-avail">Live · GPT-5.2</span>
            </div>
            <h2 className="font-display font-semibold text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[1] text-white">
              The intelligence<br />
              layer for private<br />
              <span className="text-sky">aviation.</span>
            </h2>
            <p className="mt-6 text-lg text-white/70 max-w-xl leading-relaxed">
              Maximum domains of aviation knowledge — aircraft, FAA, airport, career, maintenance,
              industry, resume writing, airspace. AI answers strengthened by the wisdom of 26,500+
              professional crews. <span className="text-white/85">PJP GPT hive mind in your hands
              for less than an OpenAI subscription.</span>
            </p>
            <div className="mt-8 flex flex-wrap gap-2">
              {["FAA Regulations", "Aircraft Systems", "Airport Intel", "Career", "Maintenance", "Industry", "Resume Writing", "Airspace"].map((t) => (
                <span key={t} className="chip chip-neutral">{t}</span>
              ))}
            </div>
            <div className="mt-10 flex gap-3 flex-wrap">
              <Link to="/aerointel" data-testid="cta-aerointel" className="btn btn-primary">
                Ask AeroIntel <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link to="/membership" data-testid="cta-membership-intel" className="btn">
                Join from $129/yr
              </Link>
            </div>
          </div>

          <div className="md:col-span-5 animate-gentle-float">
            <div className="relative rounded-2xl border border-cyan/30 bg-obsidian/85 backdrop-blur-md p-7 overflow-hidden shadow-[0_0_50px_-12px_rgba(75,156,255,0.35)]" data-testid="trending-now-card">
              {/* Ambient glow */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(circle at 20% 0%, rgba(75,156,255,0.20), transparent 55%), radial-gradient(circle at 100% 100%, rgba(255,138,61,0.10), transparent 55%)",
                }}
              />
              {/* Scanning line */}
              <div className="absolute inset-x-0 top-0 h-px overflow-hidden">
                <div className="h-full w-1/3 bg-gradient-to-r from-transparent via-cyan to-transparent animate-[scanline_3.6s_linear_infinite]" />
              </div>
              {/* HUD corners */}
              <span className="hud-c-tl" /><span className="hud-c-tr" />
              <span className="hud-c-bl" /><span className="hud-c-br" />

              <div className="relative flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <span className="relative inline-flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-cyan opacity-70 animate-ping" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-cyan shadow-[0_0_8px_rgba(75,156,255,0.9)]" />
                  </span>
                  <div className="mono text-[10px] text-cyan tracking-[0.28em] uppercase">// TRENDING · LIVE FEED</div>
                </div>
                <span className="mono text-[9px] tracking-widest text-white/45 uppercase">HIVE MIND</span>
              </div>

              <div className="relative space-y-1">
                {[
                  { icon: TrendingUp,  label: "Most searched",       value: "Gulfstream G700",       delta: "+24%",  tone: "cyan" },
                  { icon: ShieldCheck, label: "Newest FAA guidance", value: "AC 120-76E EFB Update", delta: "NEW",   tone: "orange" },
                  { icon: Wrench,      label: "Trending maint SB",   value: "G650 Thrust Reverser",  delta: "+18%",  tone: "cyan" },
                  { icon: Plane,       label: "Latest hiring",       value: "Part 135 PIC — KPWK",   delta: "2h",    tone: "avail" },
                  { icon: Sparkles,    label: "Charter demand",      value: "KTEB → KMIA",           delta: "HIGH",  tone: "orange" },
                ].map((r, i) => {
                  const ring = r.tone === "orange" ? "border-orange-hud/40 bg-orange-hud/[0.08]" : r.tone === "avail" ? "border-avail/40 bg-avail/[0.08]" : "border-cyan/40 bg-cyan/[0.08]";
                  const iconColor = r.tone === "orange" ? "text-orange-hud" : r.tone === "avail" ? "text-avail" : "text-cyan";
                  const deltaColor = r.tone === "orange" ? "text-orange-hud bg-orange-hud/10 border-orange-hud/30" : r.tone === "avail" ? "text-avail bg-avail/10 border-avail/30" : "text-cyan bg-cyan/10 border-cyan/30";
                  return (
                    <div
                      key={i}
                      className="group flex items-center gap-4 py-3 border-b border-white/[0.05] last:border-0 hover:bg-white/[0.02] transition-colors -mx-2 px-2 rounded-md"
                    >
                      <div className={`h-11 w-11 rounded-lg border ${ring} flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform`}>
                        <r.icon className={`h-[18px] w-[18px] ${iconColor}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="mono text-[10px] uppercase tracking-[0.22em] text-white/45 mb-0.5">{r.label}</div>
                        <div className="font-display font-semibold text-[17px] text-white truncate leading-tight">{r.value}</div>
                      </div>
                      <span className={`mono text-[10px] font-semibold tracking-wider px-2 py-1 rounded-md border ${deltaColor} flex-shrink-0`}>
                        {r.delta}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Footer strip */}
              <div className="relative mt-5 pt-3 border-t border-white/[0.06] flex items-center justify-between">
                <div className="mono text-[9px] tracking-[0.24em] uppercase text-white/45">
                  Updated <span className="text-cyan">just now</span>
                </div>
                <div className="mono text-[9px] tracking-[0.24em] uppercase text-white/45">
                  <span className="text-cyan">26,511</span> crews contributing
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ NUMBERS BAND ============ */}
      <section className="border-t border-white/[0.06] py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="eyebrow mb-5">By the numbers</div>
          <h2 className="font-display font-semibold text-4xl md:text-6xl tracking-tight leading-[1.05] text-white max-w-4xl mb-16">
            One network. Every aircraft. Every role. Every airport.
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-10 md:gap-8">
            {[
              { icon: Users, n: (stats.members || 26511).toLocaleString() + "+", l: "Verified members" },
              { icon: Globe2, n: "120+", l: "Countries covered" },
              { icon: Plane, n: "1,200+", l: "Industry roles" },
              { icon: Zap, n: "< 15 min", l: "Avg. AOG response" },
            ].map((s, i) => (
              <div key={i} className="animate-fade-up min-w-0" style={{ animationDelay: `${i * 0.08}s` }}>
                <s.icon className="h-5 w-5 text-sky mb-4" />
                <div className="font-display font-semibold text-[28px] sm:text-[32px] md:text-4xl lg:text-5xl text-white tracking-tight leading-none whitespace-nowrap">{s.n}</div>
                <div className="mt-2 text-[13px] text-white/55">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ TESTIMONIAL ============ */}
      <section className="relative border-t border-white/[0.06] overflow-hidden py-24 md:py-32">
        <div className="absolute inset-0 opacity-15" style={{ backgroundImage: `linear-gradient(180deg, rgba(10,15,26,0.95), rgba(10,15,26,1)), url(${NIGHT_TARMAC})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <div className="eyebrow mx-auto mb-8 justify-center">Member voice</div>
          <blockquote className="font-display font-medium text-3xl md:text-5xl lg:text-6xl leading-[1.1] tracking-tight text-white">
            "PJP connected me with a Citation X operator I'd been trying to reach for two years.
            Within a week of joining, I had the introduction.<br />
            <span className="text-sky">This network is the real deal.</span>"
          </blockquote>
          <div className="mt-12 flex items-center justify-center gap-4">
            <div className="h-11 w-11 rounded-full bg-sky/15 border border-sky/30 flex items-center justify-center">
              <Plane className="h-4 w-4 text-sky" />
            </div>
            <div className="text-left">
              <div className="text-white font-medium">Captain M. Rivera</div>
              <div className="text-[13px] text-white/55 mt-0.5">Citation X · 12,400 hrs · FAA Verified</div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ MEMBERSHIP CTA ============ */}
      <section className="border-t border-white/[0.06] py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="eyebrow mx-auto mb-6 justify-center">Annual membership</div>
          <h2 className="font-display font-semibold text-5xl md:text-7xl lg:text-8xl tracking-tight leading-[0.98] text-white">
            Take your seat<br /><span className="text-sky">in the network.</span>
          </h2>
          <p className="mt-8 text-lg text-white/70 max-w-2xl mx-auto leading-relaxed">
            One annual membership. Full access to the private PJP community, AeroIntel,
            the Job Board, verified Directory, and every corner of private aviation.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/membership" data-testid="cta-become-member" className="btn btn-primary">
              Become a member <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link to="/aerointel" data-testid="cta-explore-intel" className="btn">
              Explore AeroIntel
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function BragStat({ value, label, tone }) {
  return (
    <div className="text-center md:text-left">
      <div
        className="font-display font-bold text-3xl md:text-4xl tabular-nums leading-none"
        style={{ color: tone, textShadow: `0 0 20px ${tone}55` }}
      >
        {value}
      </div>
      <div className="mono text-[10px] tracking-widest uppercase text-white/55 mt-1.5">{label}</div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// FEATURE TOUR — alternating rows + inline UI mockups
// ═══════════════════════════════════════════════════════════════════════════
function FeatureRow({ idx, accent, overline, title, body, chips, linkTo, linkLabel, mock, flip }) {
  return (
    // GLASSMORPHIC CARD — each row is a frosted-glass panel with a subtle
    // accent-colored glow blob behind it so the backdrop-blur actually renders
    // that beautiful chromatic frosted look on top of dark obsidian.
    <div className="relative isolate">
      {/* Accent-colored glow blobs sitting behind the glass — these are what
          the backdrop-blur actually blurs, creating the color-tinted frosted
          effect. Positioned + scaled so both sides of the row get color. */}
      <div
        aria-hidden="true"
        className="absolute -z-10 top-1/2 left-1/4 -translate-x-1/2 -translate-y-1/2 w-[520px] h-[520px] rounded-full opacity-45 pointer-events-none"
        style={{ background: `radial-gradient(circle, ${accent}66 0%, transparent 70%)`, filter: "blur(80px)" }}
      />
      <div
        aria-hidden="true"
        className="absolute -z-10 top-1/3 right-0 translate-x-1/4 -translate-y-1/2 w-[420px] h-[420px] rounded-full opacity-30 pointer-events-none"
        style={{ background: `radial-gradient(circle, ${accent}55 0%, transparent 70%)`, filter: "blur(90px)" }}
      />

      <div
        className={`
          relative rounded-3xl p-6 md:p-10 lg:p-12
          bg-white/[0.03] backdrop-blur-2xl backdrop-saturate-150
          border border-white/[0.08]
          shadow-[0_1px_0_rgba(255,255,255,0.04)_inset,0_-1px_0_rgba(0,0,0,0.25)_inset,0_40px_60px_-30px_rgba(0,0,0,0.4)]
          transition-all duration-500 hover:border-white/[0.14] hover:bg-white/[0.045]
        `}
        style={{ boxShadow: `0 0 0 1px rgba(255,255,255,0.02) inset, 0 30px 60px -20px rgba(0,0,0,0.5), 0 0 80px -40px ${accent}55` }}
      >
        {/* Top highlight edge — the "light catches the top of the glass" trick */}
        <div
          aria-hidden="true"
          className="absolute top-0 left-6 right-6 h-px pointer-events-none"
          style={{ background: `linear-gradient(90deg, transparent, ${accent}66, transparent)`, opacity: 0.6 }}
        />

        <div className={`relative grid md:grid-cols-12 gap-8 md:gap-12 items-center ${flip ? "md:[&>*:first-child]:order-2" : ""}`}>
          {/* Copy side */}
          <div className="md:col-span-5">
            <div className="flex items-baseline gap-3 mb-3">
              <div
                className="font-display font-bold text-5xl leading-none tabular-nums"
                style={{ color: accent, textShadow: `0 0 24px ${accent}55` }}
              >
                {idx}
              </div>
              <div className="mono text-[9.5px] tracking-[0.22em] uppercase" style={{ color: `${accent}CC` }}>
                {overline}
              </div>
            </div>
            <h3 className="font-display font-semibold text-3xl md:text-[42px] tracking-tight leading-[1.05] text-white mb-4">
              {title}
            </h3>
            <p className="text-white/70 text-[15px] leading-relaxed">{body}</p>
            <div className="mt-5 flex flex-wrap gap-1.5">
              {chips.map((c) => (
                <span
                  key={c}
                  className="mono text-[9.5px] tracking-widest uppercase px-2 py-1 rounded-full border backdrop-blur-md"
                  style={{ borderColor: `${accent}55`, background: `${accent}12`, color: accent }}
                >
                  {c}
                </span>
              ))}
            </div>
            <Link
              to={linkTo}
              data-testid={`feature-tour-cta-${idx}`}
              className="inline-flex items-center gap-2 mt-7 mono text-[11px] tracking-widest uppercase font-bold hover:gap-3 transition-all"
              style={{ color: accent }}
            >
              {linkLabel} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {/* Visual mock side — kept its accent gradient card, now stacked
              INSIDE the outer glass panel for a layered frosted feel. */}
          <div className="md:col-span-7">
            <div
              className="relative rounded-2xl border overflow-hidden backdrop-blur-md"
              style={{
                borderColor: `${accent}44`,
                background: `linear-gradient(140deg, ${accent}10 0%, rgba(5,8,16,0.85) 60%)`,
                boxShadow: `0 0 60px -30px ${accent}66`,
              }}
            >
              {/* Corner brackets */}
              <span className="absolute top-3 left-3 w-3 h-3 border-t border-l z-10" style={{ borderColor: accent }} />
              <span className="absolute top-3 right-3 w-3 h-3 border-t border-r z-10" style={{ borderColor: accent }} />
              <span className="absolute bottom-3 left-3 w-3 h-3 border-b border-l z-10" style={{ borderColor: accent }} />
              <span className="absolute bottom-3 right-3 w-3 h-3 border-b border-r z-10" style={{ borderColor: accent }} />

              <div className="p-6 md:p-8">
                {mock}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Mock building blocks ─────────────────────────────────────────────────────
function MockChrome({ label, color }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <span className="h-2 w-2 rounded-full" style={{ background: color }} />
      <span className="mono text-[9px] tracking-widest uppercase" style={{ color: `${color}CC` }}>{label}</span>
      <div className="flex-1" />
      <span className="mono text-[9px] tracking-widest text-white/30">PJP-NET · LIVE</span>
    </div>
  );
}

function MockHotMic() {
  const topics = [
    { title: "G450 · Fadec fault code after CB-pop — normal?", author: "SarahC · G4/G5", replies: 14, tag: "TECHNICAL" },
    { title: "Anyone flown FL410 westbound over the ITCZ lately?", author: "ContractCPT · GLEX", replies: 27, tag: "OPS" },
    { title: "Best hotel near KMEM for a 24hr layover?", author: "FA_Nicole · WW", replies: 41, tag: "LAYOVERS" },
    { title: "IA looking for XLS+ MSP-approved shop in TX", author: "AMT_Mike · TX", replies: 8, tag: "MAINT" },
  ];
  return (
    <div>
      <MockChrome label="// HOT MIC · TOPICS" color="#C99BFF" />
      <div className="space-y-2">
        {topics.map((t, i) => (
          <div key={i} className="rounded-lg border border-white/10 bg-black/40 px-3 py-2 flex items-center gap-3">
            <span className="mono text-[8.5px] tracking-widest px-1.5 py-0.5 rounded" style={{ background: "#C99BFF22", color: "#C99BFF", border: "1px solid #C99BFF44" }}>
              {t.tag}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-white/90 text-[13px] truncate">{t.title}</div>
              <div className="mono text-[9px] text-white/45 mt-0.5">{t.author}</div>
            </div>
            <div className="mono text-[10px] text-white/55">{t.replies} <MessageSquare className="inline h-3 w-3 ml-0.5" /></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MockWallet() {
  return (
    <div>
      <MockChrome label="// PJP WALLET · BALANCE" color="#2ED573" />
      <div className="rounded-xl border border-white/10 bg-black/50 p-5">
        <div className="mono text-[9px] tracking-widest uppercase text-white/50">Available balance</div>
        <div className="font-display font-bold text-[44px] leading-none mt-1" style={{ color: "#2ED573", textShadow: "0 0 20px #2ED57366" }}>
          $18,420<span className="text-white/50">.<span className="text-xl">00</span></span>
        </div>
        <div className="mono text-[10px] tracking-widest uppercase text-white/40 mt-1">USD · earned your last trip</div>
      </div>
      <div className="mt-3 space-y-1.5">
        {[
          { d: "Aug 12", label: "Contract PIC · N578GA · KTEB→KMIA", amt: "+ $8,200" },
          { d: "Aug 03", label: "Empty-leg fill · G550 · KJFK→KOPF (× 4 seats)", amt: "+ $6,800" },
          { d: "Jul 27", label: "Referral bonus · Mike A. (AMT) · signed", amt: "+ $200" },
          { d: "Jul 20", label: "Payout · ACH · Chase ×4421", amt: "− $12,000" },
        ].map((r, i) => (
          <div key={i} className="flex items-center gap-3 px-2 py-1 rounded hover:bg-white/[0.03]">
            <span className="mono text-[9px] text-white/40 w-14">{r.d}</span>
            <span className="text-[12px] text-white/75 flex-1 truncate">{r.label}</span>
            <span className={`mono text-[11px] font-bold ${r.amt.startsWith("+") ? "text-emerald-400" : "text-white/50"}`}>{r.amt}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MockUnicom() {
  const threads = [
    { from: "N578GA → Signature KOPF", body: "Fuel · 4,200 gal Jet-A · arrival 14:20L · rewards code attached", time: "just now", tag: "FUEL", tagColor: "#F4C443" },
    { from: "N922LX → Atlantic KTEB",  body: "Ground · Suburban x1 · 4 PAX · direct to Mandarin Oriental", time: "6m",       tag: "GROUND", tagColor: "#4B9CFF" },
    { from: "N360EP → Million Air KVNY", body: "GPU + lav + potable water · block out 06:00L tomorrow",       time: "22m",      tag: "SERVICE", tagColor: "#5FE895" },
    { from: "N101PJ → JetAviation LFPB", body: "Custom clearance · dep manifest attached · arrival 09:15Z",   time: "1h",       tag: "CUSTOMS", tagColor: "#C99BFF" },
  ];
  return (
    <div>
      <MockChrome label="// UNICOM · N578GA · KOPF INBOUND" color="#4B9CFF" />
      <div className="space-y-2">
        {threads.map((t, i) => (
          <div key={i} className="rounded-lg border border-white/10 bg-black/40 px-3 py-2.5 flex items-start gap-3">
            <span className="mono text-[8.5px] tracking-widest px-1.5 py-0.5 rounded flex-shrink-0 mt-0.5" style={{ background: `${t.tagColor}22`, color: t.tagColor, border: `1px solid ${t.tagColor}55` }}>
              {t.tag}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-2">
                <div className="mono text-[10.5px] tracking-widest text-white/85 truncate">{t.from}</div>
                <div className="mono text-[9px] text-white/40 flex-shrink-0">{t.time}</div>
              </div>
              <div className="text-[12.5px] text-white/70 mt-0.5 truncate">{t.body}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MockFuel() {
  const rows = [
    { icao: "KOPF", city: "Opa-Locka, FL", retail: 6.85, pjp: 2.11, save: "− 69%" },
    { icao: "KTEB", city: "Teterboro, NJ", retail: 8.20, pjp: 3.42, save: "− 58%" },
    { icao: "KVNY", city: "Van Nuys, CA", retail: 7.90, pjp: 3.15, save: "− 60%" },
    { icao: "KASE", city: "Aspen, CO",     retail: 9.15, pjp: 4.80, save: "− 47%" },
    { icao: "KAPF", city: "Naples, FL",    retail: 7.45, pjp: 2.68, save: "− 64%" },
  ];
  return (
    <div>
      <MockChrome label="// PJP FUEL · LIVE BOARD" color="#F4C443" />
      <div className="rounded-xl border border-white/10 bg-black/50 overflow-hidden">
        <div className="grid grid-cols-[70px_1fr_70px_70px_60px] gap-2 px-3 py-2 mono text-[9px] tracking-widest uppercase text-white/45 border-b border-white/10">
          <div>ICAO</div><div>CITY</div><div className="text-right">RETAIL</div><div className="text-right">PJP</div><div className="text-right">SAVE</div>
        </div>
        {rows.map((r) => (
          <div key={r.icao} className="grid grid-cols-[70px_1fr_70px_70px_60px] gap-2 px-3 py-2 items-center border-b border-white/[0.04] last:border-b-0">
            <div className="mono text-[11px] tracking-widest text-cyan">{r.icao}</div>
            <div className="text-[12px] text-white/70 truncate">{r.city}</div>
            <div className="mono text-[11px] text-white/40 line-through text-right">${r.retail.toFixed(2)}</div>
            <div className="mono text-[12px] font-bold text-right" style={{ color: "#F4C443" }}>${r.pjp.toFixed(2)}</div>
            <div className="mono text-[10px] font-bold text-right" style={{ color: "#2ED573" }}>{r.save}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MockPHF() {
  const cats = [
    { k: "TOTAL", v: "8,432" },
    { k: "PIC", v: "6,110" },
    { k: "SIC", v: "2,322" },
    { k: "TURBINE", v: "8,120" },
    { k: "JET", v: "7,890" },
    { k: "NIGHT", v: "1,255" },
    { k: "IFR ACTUAL", v: "941" },
    { k: "CROSS-COUNTRY", v: "5,203" },
  ];
  const types = [
    { t: "GLF-VII (G450)", h: 2100 },
    { t: "GLF-VII (G550)", h: 1580 },
    { t: "GLEX", h: 640 },
    { t: "CE-560XL", h: 812 },
  ];
  return (
    <div>
      <MockChrome label="// PILOT HISTORY · SNAPSHOT" color="#FF6B9D" />
      <div className="grid grid-cols-4 gap-2">
        {cats.map((c) => (
          <div key={c.k} className="rounded-lg border border-white/10 bg-black/40 p-2">
            <div className="mono text-[8.5px] tracking-widest uppercase text-white/45">{c.k}</div>
            <div className="font-display font-bold text-white text-lg leading-none mt-1 tabular-nums">{c.v}</div>
          </div>
        ))}
      </div>
      <div className="mt-3">
        <div className="mono text-[9px] tracking-widest uppercase text-white/45 mb-1.5">// TYPE-SPECIFIC HOURS</div>
        <div className="space-y-1.5">
          {types.map((t) => (
            <div key={t.t} className="flex items-center gap-3">
              <span className="mono text-[10.5px] text-white/70 w-32 flex-shrink-0">{t.t}</span>
              <div className="flex-1 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${Math.min(100, t.h / 25)}%`, background: "#FF6B9D" }} />
              </div>
              <span className="mono text-[11px] font-bold tabular-nums w-14 text-right" style={{ color: "#FF6B9D" }}>{t.h.toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MockLogbook() {
  const legs = [
    { d: "08/12", tail: "N578GA", route: "KTEB → KMIA", role: "PIC", hrs: "2.8", type: "G550" },
    { d: "08/09", tail: "N922LX", route: "KHPN → KLAS", role: "PIC", hrs: "4.6", type: "GLEX" },
    { d: "08/05", tail: "N578GA", route: "KMIA → KTEB", role: "PIC", hrs: "2.9", type: "G550" },
    { d: "08/01", tail: "N360BC", route: "KAPF → KJFK", role: "SIC", hrs: "3.2", type: "CE-680" },
  ];
  return (
    <div>
      <MockChrome label="// LOGBOOK · RECENT LEGS" color="#5FE895" />
      <div className="rounded-xl border border-white/10 bg-black/50 overflow-hidden">
        <div className="grid grid-cols-[55px_75px_1fr_50px_45px_60px] gap-2 px-3 py-2 mono text-[9px] tracking-widest uppercase text-white/45 border-b border-white/10">
          <div>DATE</div><div>TAIL</div><div>ROUTE</div><div>ROLE</div><div className="text-right">HRS</div><div className="text-right">TYPE</div>
        </div>
        {legs.map((l, i) => (
          <div key={i} className="grid grid-cols-[55px_75px_1fr_50px_45px_60px] gap-2 px-3 py-2 items-center border-b border-white/[0.04] last:border-b-0">
            <div className="mono text-[10.5px] text-white/60">{l.d}</div>
            <div className="mono text-[11px] tracking-widest" style={{ color: "#5FE895" }}>{l.tail}</div>
            <div className="text-[12px] text-white/85 truncate">{l.route}</div>
            <div className="mono text-[10.5px] text-white/70">{l.role}</div>
            <div className="mono text-[11px] font-bold text-right text-white">{l.hrs}</div>
            <div className="mono text-[10px] text-right text-white/60">{l.type}</div>
          </div>
        ))}
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2">
        <div className="rounded-lg border border-white/10 bg-black/40 px-2 py-1.5">
          <div className="mono text-[8.5px] uppercase text-white/45">Currency · 3 day/night</div>
          <div className="mono text-[11px] font-bold text-emerald-400">CURRENT · 27 days</div>
        </div>
        <div className="rounded-lg border border-white/10 bg-black/40 px-2 py-1.5">
          <div className="mono text-[8.5px] uppercase text-white/45">Medical class 1</div>
          <div className="mono text-[11px] font-bold text-white/85">EXP · 2027-03-14</div>
        </div>
        <div className="rounded-lg border border-white/10 bg-black/40 px-2 py-1.5">
          <div className="mono text-[8.5px] uppercase text-white/45">BFR · 61.56</div>
          <div className="mono text-[11px] font-bold text-white/85">DUE · 2027-01-08</div>
        </div>
      </div>
    </div>
  );
}

function MockAircraftSales() {
  const listings = [
    { model: "2019 Gulfstream G550", tail: "N578GA", hrs: "2,400 TT", price: "$32.5M", tag: "PRE-MARKET" },
    { model: "2015 Bombardier Global 6000", tail: "N922LX", hrs: "4,180 TT", price: "$27.9M", tag: "FRESH LISTING" },
    { model: "2020 Embraer Phenom 300E", tail: "N360EP", hrs: "1,100 TT", price: "$10.2M", tag: "A&P INSPECTED" },
  ];
  return (
    <div>
      <MockChrome label="// AIRCRAFT SALES · FLOOR" color="#FFD86B" />
      <div className="space-y-2.5">
        {listings.map((l) => (
          <div key={l.tail} className="rounded-lg border border-white/10 bg-black/40 p-3 flex items-center gap-4">
            {/* Faux aircraft silhouette */}
            <div className="h-12 w-16 rounded flex-shrink-0 flex items-center justify-center" style={{ background: "linear-gradient(135deg, #FFD86B22, #FFD86B08)" }}>
              <Plane className="h-6 w-6" style={{ color: "#FFD86B" }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-semibold text-white text-[15px] truncate">{l.model}</div>
              <div className="mono text-[10.5px] text-white/50 mt-0.5">{l.tail} · {l.hrs}</div>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="font-display font-bold text-lg" style={{ color: "#FFD86B" }}>{l.price}</div>
              <div className="mono text-[8.5px] tracking-widest mt-0.5" style={{ color: "#FFD86BCC" }}>{l.tag}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MockQuote() {
  return (
    <div>
      <MockChrome label="// INSTANT PJ QUOTE · KOPF → KASE" color="#4B9CFF" />
      <div className="rounded-xl border border-white/10 bg-black/50 p-4 relative overflow-hidden">
        {/* Faux route arc */}
        <svg viewBox="0 0 400 90" className="w-full h-24" preserveAspectRatio="none">
          <defs>
            <linearGradient id="q-arc" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor="#4B9CFF" stopOpacity="0.1" />
              <stop offset="0.5" stopColor="#4B9CFF" stopOpacity="0.9" />
              <stop offset="1" stopColor="#4B9CFF" stopOpacity="0.1" />
            </linearGradient>
          </defs>
          <path d="M 20 70 Q 200 -10 380 70" fill="none" stroke="url(#q-arc)" strokeWidth="1.5" strokeDasharray="4 4" />
          <circle cx="20" cy="70" r="4" fill="#4B9CFF" />
          <circle cx="380" cy="70" r="4" fill="#4B9CFF" />
          <text x="20" y="86" fill="#4B9CFF" fontSize="10" fontFamily="monospace" textAnchor="middle">KOPF</text>
          <text x="380" y="86" fill="#4B9CFF" fontSize="10" fontFamily="monospace" textAnchor="middle">KASE</text>
        </svg>
        <div className="grid grid-cols-4 gap-3 mt-2">
          <QuoteStat label="DIST" val="1,842 nm" />
          <QuoteStat label="FLIGHT TIME" val="3h 42m" />
          <QuoteStat label="AIRCRAFT" val="G550" />
          <QuoteStat label="PAX" val="6" />
        </div>
        <div className="mt-4 pt-4 border-t border-white/10 flex items-baseline justify-between">
          <div>
            <div className="mono text-[9px] tracking-widest uppercase text-white/50">All-in quote · wind + tanker adjusted</div>
            <div className="font-display font-bold text-3xl mt-1" style={{ color: "#4B9CFF", textShadow: "0 0 20px #4B9CFF55" }}>
              $54,820
            </div>
          </div>
          <div className="mono text-[9px] tracking-widest uppercase text-emerald-400 text-right">
            <div>SAVED · $8.2K</div>
            <div className="text-white/45 mt-1">vs. broker retail</div>
          </div>
        </div>
      </div>
    </div>
  );
}
function QuoteStat({ label, val }) {
  return (
    <div className="rounded border border-white/10 bg-black/40 p-2">
      <div className="mono text-[8.5px] tracking-widest uppercase text-white/45">{label}</div>
      <div className="mono text-[11px] font-bold text-white mt-0.5">{val}</div>
    </div>
  );
}

function MockEmptyLegs() {
  const legs = [
    { d: "Aug 12 · 09:30", route: "KTEB → KMIA", ac: "G550", price: "$3,200", seats: "6 left" },
    { d: "Aug 14 · 11:00", route: "KMIA → MYNN", ac: "Citation X", price: "$1,900", seats: "4 left" },
    { d: "Aug 21 · 08:00", route: "KBED → KJAC", ac: "Global 6000", price: "$5,800", seats: "8 left" },
    { d: "Aug 27 · 22:00", route: "KJFK → EGLF", ac: "G650",  price: "$12,500", seats: "12 left" },
  ];
  return (
    <div>
      <MockChrome label="// JUMPSEAT · LIVE CALENDAR" color="#2ED573" />
      <div className="space-y-2">
        {legs.map((l, i) => (
          <div key={i} className="rounded-lg border border-white/10 bg-black/40 px-3 py-2.5 flex items-center gap-3">
            <CalIcon className="h-3.5 w-3.5" style={{ color: "#2ED573" }} />
            <div className="mono text-[10.5px] tracking-widest" style={{ color: "#2ED573" }}>{l.d}</div>
            <div className="font-display font-semibold text-white text-[14px] flex-1 truncate">{l.route}</div>
            <div className="text-[11px] text-white/60 hidden md:block">{l.ac}</div>
            <div className="mono text-[10px] text-white/55">{l.seats}</div>
            <div className="font-display font-bold text-right" style={{ color: "#2ED573" }}>{l.price}<span className="text-[9px] text-white/50 font-normal">/seat</span></div>
          </div>
        ))}
      </div>
      <div className="mt-3 rounded-lg border p-2.5 flex items-center gap-2" style={{ borderColor: "#2ED57344", background: "#2ED57310" }}>
        <ShieldCheck className="h-4 w-4" style={{ color: "#2ED573" }} />
        <div className="mono text-[10.5px] tracking-widest uppercase" style={{ color: "#2ED573" }}>
          FREE FOR CREW · $10K/YR RIDER JUMPSEAT WAIVED
        </div>
      </div>
    </div>
  );
}

function MockCrewSearch() {
  const crews = [
    { name: "S. Chen",   base: "KTEB", cert: "ATP · G550 · GLEX", stat: "AVAILABLE", tone: "#2ED573" },
    { name: "M. Alvarez", base: "KVNY", cert: "A&P · IA · G550 · GLEX", stat: "AVAILABLE", tone: "#2ED573" },
    { name: "R. Torres",  base: "KMIA", cert: "ATP · CE-680 · CE-750", stat: "AVAILABLE", tone: "#2ED573" },
    { name: "K. Wolfe",   base: "KAPF", cert: "FA · WW · Falcon 900", stat: "ON TRIP",   tone: "#F4C443" },
  ];
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <SearchIcon className="h-3.5 w-3.5" style={{ color: "#FF7A7A" }} />
        <div className="mono text-[9px] tracking-widest uppercase" style={{ color: "#FF7A7A" }}>
          FIND-CREW · G550 · KVNY · 200nm · FAA-VERIFIED
        </div>
        <div className="flex-1" />
        <span className="mono text-[9px] tracking-widest text-white/40">3 MATCHES · 87ms</span>
      </div>
      <div className="space-y-2">
        {crews.map((c, i) => (
          <div key={i} className="rounded-lg border border-white/10 bg-black/40 px-3 py-2.5 flex items-center gap-3">
            <div className="h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "#FF7A7A22", border: "1px solid #FF7A7A55" }}>
              <span className="mono text-[10px] font-bold" style={{ color: "#FF7A7A" }}>{c.name.split(" ")[0][0]}{c.name.split(" ")[1][0]}</span>
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-display font-semibold text-white text-[14px]">{c.name}</span>
                <span className="mono text-[9.5px] tracking-widest text-white/50">· {c.base}</span>
              </div>
              <div className="mono text-[10.5px] text-white/60 truncate">{c.cert}</div>
            </div>
            <span className="mono text-[9px] tracking-widest uppercase px-2 py-0.5 rounded-full border" style={{ color: c.tone, borderColor: `${c.tone}55`, background: `${c.tone}12` }}>
              {c.stat}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
